import React from "react";

/**
 * DetailDiagram (web) - mirror of mobile DetailDiagram.
 * Clear, dimensioned construction-detail sketches (inline SVG) shown inside
 * the InfoSheet so tapping ANY element gives a labelled drawing, not just text.
 */

const INK = "#0F172A";
const BLUE = "#2563EB";
const RED = "#DC2626";
const GREEN = "#059669";
const AMBER = "#D97706";
const GREY = "#94A3B8";

function Dim({ x1, y1, x2, y2, label, color = BLUE, above = true }) {
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={1} />
      <line x1={x1} y1={y1 - 3} x2={x1} y2={y1 + 3} stroke={color} strokeWidth={1} />
      <line x1={x2} y1={y2 - 3} x2={x2} y2={y2 + 3} stroke={color} strokeWidth={1} />
      <text x={mx} y={above ? my - 4 : my + 9} fill={color} fontSize={9} fontWeight="700" textAnchor="middle">{label}</text>
    </g>
  );
}
function Note({ x, y, text, color = INK, anchor = "start" }) {
  return <text x={x} y={y} fill={color} fontSize={8.5} fontWeight="700" textAnchor={anchor}>{text}</text>;
}

function render(kind, ar) {
  const L = (a, e) => (ar ? a : e);
  switch (kind) {
    case "wall_exterior":
      return (<>
        <rect x={40} y={60} width={8} height={70} fill="#E2E8F0" stroke={INK} strokeWidth={1} />
        <rect x={48} y={60} width={90} height={70} fill="#FCD9B6" stroke={INK} strokeWidth={1} />
        <rect x={138} y={60} width={28} height={70} fill="#FEF08A" stroke={INK} strokeWidth={1} />
        <rect x={166} y={60} width={50} height={70} fill="#FCD9B6" stroke={INK} strokeWidth={1} />
        <rect x={216} y={60} width={8} height={70} fill="#E2E8F0" stroke={INK} strokeWidth={1} />
        <line x1={140} y1={62} x2={164} y2={128} stroke={GREY} strokeWidth={0.6} />
        <Note x={20} y={98} text={L("خارج", "OUT")} color={RED} />
        <Note x={228} y={98} text={L("داخل", "IN")} color={GREEN} />
        <Dim x1={48} y1={48} x2={138} y2={48} label={L("بلوك 20سم", "20cm block")} />
        <Dim x1={138} y1={140} x2={166} y2={140} label={L("عزل 5سم", "5cm ins.")} above={false} color={AMBER} />
        <Dim x1={166} y1={48} x2={216} y2={48} label={L("داخلي 10سم", "10cm")} />
        <Note x={132} y={160} text={L("مقطع أفقي للجدار الخارجي", "Exterior wall - plan cut")} color={GREY} anchor="middle" />
      </>);
    case "wall_interior":
      return (<>
        <rect x={90} y={40} width={12} height={110} fill="#E2E8F0" stroke={INK} strokeWidth={1} />
        <rect x={102} y={40} width={40} height={110} fill="#FCD9B6" stroke={INK} strokeWidth={1} />
        <rect x={142} y={40} width={12} height={110} fill="#E2E8F0" stroke={INK} strokeWidth={1} />
        <Dim x1={102} y1={28} x2={142} y2={28} label={L("بلوك 10-15سم", "10-15cm block")} />
        <Note x={122} y={170} text={L("لياسة الوجهين + دهان", "Plaster both faces + paint")} color={GREY} anchor="middle" />
      </>);
    case "column":
      return (<>
        <rect x={110} y={50} width={90} height={90} fill="#CBD5E1" stroke={INK} strokeWidth={1.5} />
        <rect x={120} y={60} width={70} height={70} fill="none" stroke={RED} strokeWidth={1.2} />
        {[[122,62],[155,62],[188,62],[122,95],[188,95],[122,128],[155,128],[188,128]].map(([cx,cy],i)=>(<circle key={i} cx={cx} cy={cy} r={3.4} fill={INK} />))}
        <Dim x1={110} y1={38} x2={200} y2={38} label={L("مثال 30×30سم", "e.g. 30×30cm")} />
        <Note x={210} y={66} text={L("كانة Ø8", "Ø8 tie")} color={RED} />
        <Note x={210} y={100} text={L("8 قضبان Ø14-20", "8 bars Ø14-20")} color={INK} />
        <Note x={155} y={160} text={L("غطاء خرساني ≥ 40مم", "Cover ≥ 40mm")} color={GREY} anchor="middle" />
      </>);
    case "beam":
      return (<>
        <rect x={120} y={45} width={70} height={100} fill="#CBD5E1" stroke={INK} strokeWidth={1.5} />
        <rect x={128} y={53} width={54} height={84} fill="none" stroke={RED} strokeWidth={1.1} />
        {[[132,58],[155,58],[178,58]].map(([cx,cy],i)=>(<circle key={"t"+i} cx={cx} cy={cy} r={3} fill={INK} />))}
        {[[132,132],[155,132],[178,132]].map(([cx,cy],i)=>(<circle key={"b"+i} cx={cx} cy={cy} r={3.4} fill={INK} />))}
        <Dim x1={120} y1={33} x2={190} y2={33} label={L("العرض 20-30", "width 20-30")} />
        <Dim x1={205} y1={45} x2={205} y2={145} label={L("العمق L/10", "depth L/10")} color={GREEN} />
      </>);
    case "footing_isolated":
      return (<>
        <line x1={20} y1={55} x2={300} y2={55} stroke={AMBER} strokeWidth={1.5} />
        <rect x={95} y={120} width={130} height={26} fill="#CBD5E1" stroke={INK} strokeWidth={1.4} />
        <rect x={145} y={58} width={30} height={62} fill="#94A3B8" stroke={INK} strokeWidth={1.2} />
        <line x1={150} y1={62} x2={150} y2={140} stroke={RED} strokeWidth={1.1} />
        <line x1={170} y1={62} x2={170} y2={140} stroke={RED} strokeWidth={1.1} />
        {[105,125,145,165,185,205].map((x,i)=>(<circle key={i} cx={x} cy={140} r={2.4} fill={INK} />))}
        <Dim x1={95} y1={158} x2={225} y2={158} label={L("عرض القاعدة", "footing width")} above={false} />
        <Dim x1={250} y1={55} x2={250} y2={120} label={L("≥ 1.2م", "≥ 1.2m")} color={GREEN} />
        <Note x={160} y={185} text={L("شبكة سفلية Ø12-16 @15سم", "Bottom mesh Ø12-16 @15cm")} color={GREY} anchor="middle" />
      </>);
    case "slab_solid":
      return (<>
        <rect x={40} y={70} width={240} height={34} fill="#CBD5E1" stroke={INK} strokeWidth={1.4} />
        {[60,90,120,150,180,210,240].map((x,i)=>(<circle key={"t"+i} cx={x} cy={78} r={2.4} fill={INK} />))}
        {[60,90,120,150,180,210,240].map((x,i)=>(<circle key={"b"+i} cx={x} cy={96} r={2.4} fill={INK} />))}
        <Dim x1={295} y1={70} x2={295} y2={104} label={L("≥15سم", "≥15cm")} color={GREEN} />
        <Note x={160} y={130} text={L("شبكتان علوية + سفلية", "Top + bottom mesh")} color={GREY} anchor="middle" />
      </>);
    case "slab_hordi":
      return (<>
        <rect x={30} y={62} width={260} height={16} fill="#CBD5E1" stroke={INK} strokeWidth={1.2} />
        {[0,1,2,3].map((i)=>{const x=40+i*62; return (<g key={i}><rect x={x} y={78} width={44} height={38} fill="#FEF3C7" stroke={INK} strokeWidth={1} /><rect x={x+44} y={78} width={12} height={38} fill="#CBD5E1" stroke={INK} strokeWidth={1} /></g>);})}
        <Note x={62} y={135} text={L("بلوك مفرّغ (هوردي)", "hollow block")} color={AMBER} />
        <Note x={225} y={135} text={L("أعصاب خرسانية", "concrete ribs")} color={INK} />
      </>);
    case "door":
      return (<>
        <rect x={120} y={30} width={70} height={130} fill="none" stroke={INK} strokeWidth={2.2} />
        <rect x={128} y={38} width={54} height={122} fill="#FDE9D0" stroke={INK} strokeWidth={1} />
        <circle cx={174} cy={100} r={2.6} fill={INK} />
        <path d="M128 160 A122 122 0 0 1 250 38" fill="none" stroke={GREEN} strokeWidth={1} strokeDasharray="4 3" />
        <line x1={128} y1={160} x2={128} y2={38} stroke={GREEN} strokeWidth={1} strokeDasharray="4 3" />
        <Dim x1={120} y1={20} x2={190} y2={20} label={L("العرض 80-100", "width 80-100")} />
        <Dim x1={108} y1={30} x2={108} y2={160} label={L("210-230سم", "210-230")} color={GREEN} />
        <Note x={205} y={150} text={L("اتجاه الفتح", "swing")} color={GREEN} />
      </>);
    case "window":
      return (<>
        <line x1={20} y1={165} x2={300} y2={165} stroke={AMBER} strokeWidth={1.4} />
        <rect x={110} y={55} width={100} height={70} fill="#DBEAFE" stroke={INK} strokeWidth={2} />
        <line x1={160} y1={55} x2={160} y2={125} stroke={INK} strokeWidth={1} />
        <Dim x1={110} y1={44} x2={210} y2={44} label={L("العرض", "width")} />
        <Dim x1={95} y1={125} x2={95} y2={165} label={L("عتبة 90سم", "sill 90cm")} color={GREEN} above={false} />
        <Note x={160} y={145} text={L("زجاج مزدوج", "double glazing")} color={BLUE} anchor="middle" />
      </>);
    case "pool":
      return (<>
        <path d="M40 55 L40 150 L250 150 L250 55" fill="#DBEAFE" stroke={INK} strokeWidth={2} />
        <line x1={40} y1={68} x2={250} y2={68} stroke={BLUE} strokeWidth={1} strokeDasharray="5 3" />
        <rect x={40} y={62} width={12} height={10} fill={RED} />
        <Note x={30} y={50} text={L("سكيمر (سحب 80%)", "skimmer 80%")} color={RED} />
        <rect x={140} y={144} width={16} height={6} fill={RED} />
        <Note x={148} y={168} text={L("مصرف القاع 20%", "main drain 20%")} color={RED} anchor="middle" />
        <polygon points="250,95 240,90 240,100" fill={GREEN} />
        <Note x={300} y={95} text={L("إرجاع (ضخ)", "return jet")} color={GREEN} anchor="end" />
        <rect x={265} y={120} width={26} height={16} fill="#E2E8F0" stroke={INK} strokeWidth={1} />
        <Note x={278} y={132} text={L("مضخة", "pump")} color={INK} anchor="middle" />
        <Note x={145} y={190} text={L("سحب ← مضخة ← فلتر ← إرجاع نظيف", "outlet → pump → filter → clean return")} color={GREY} anchor="middle" />
      </>);
    case "garden":
      return (<>
        <rect x={40} y={70} width={240} height={26} fill="#DCFCE7" stroke={INK} strokeWidth={1} />
        <rect x={40} y={96} width={240} height={8} fill="#065F46" />
        <rect x={40} y={104} width={240} height={22} fill="#E7E5E4" stroke={INK} strokeWidth={1} />
        <Note x={295} y={86} text={L("تربة 40-60سم", "soil 40-60cm")} color={GREEN} anchor="end" />
        <Note x={160} y={145} text={L("طبقة تصريف حصى 20سم", "gravel drainage 20cm")} color={GREY} anchor="middle" />
      </>);
    case "courtyard":
      return (<>
        <line x1={40} y1={40} x2={40} y2={150} stroke={INK} strokeWidth={2} />
        <line x1={250} y1={40} x2={250} y2={150} stroke={INK} strokeWidth={2} />
        <line x1={40} y1={150} x2={250} y2={150} stroke={INK} strokeWidth={2} />
        {[90,145,200].map((x,i)=>(<line key={i} x1={x} y1={140} x2={x} y2={58} stroke={RED} strokeWidth={1.2} strokeDasharray="4 3" />))}
        {[90,145,200].map((x,i)=>(<polygon key={"h"+i} points={`${x-4},62 ${x+4},62 ${x},52`} fill={RED} />))}
        <Note x={145} y={40} text={L("مكشوف للسماء - هواء ساخن يصعد", "open sky - hot air rises")} color={GREY} anchor="middle" />
      </>);
    case "balcony":
      return (<>
        <rect x={40} y={80} width={70} height={90} fill="#CBD5E1" stroke={INK} strokeWidth={1.2} />
        <rect x={110} y={95} width={140} height={16} fill="#94A3B8" stroke={INK} strokeWidth={1.4} />
        <line x1={70} y1={99} x2={245} y2={99} stroke={RED} strokeWidth={1.4} />
        <line x1={245} y1={95} x2={245} y2={45} stroke={INK} strokeWidth={1.6} />
        <line x1={165} y1={45} x2={247} y2={45} stroke={INK} strokeWidth={1.6} />
        <Dim x1={262} y1={45} x2={262} y2={95} label={L("حاجز 1.1م", "rail 1.1m")} color={GREEN} />
        <Dim x1={110} y1={130} x2={250} y2={130} label={L("عمق ≥1.2م", "depth ≥1.2m")} above={false} />
      </>);
    case "terrace":
      return (<>
        <rect x={40} y={90} width={240} height={22} fill="#CBD5E1" stroke={INK} strokeWidth={1.3} />
        <line x1={40} y1={88} x2={280} y2={88} stroke={BLUE} strokeWidth={2} />
        <path d="M60 80 L260 86" stroke={GREEN} strokeWidth={1.2} strokeDasharray="4 3" />
        <polygon points="260,86 252,82 253,90" fill={GREEN} />
        <rect x={262} y={100} width={12} height={20} fill={BLUE} />
        <Note x={130} y={72} text={L("ميل 2% نحو المصرف", "2% slope to drain")} color={GREEN} anchor="middle" />
        <Note x={160} y={135} text={L("بلاطة + عزل مائي 3مم + بلاط", "slab + 3mm waterproofing + tile")} color={GREY} anchor="middle" />
      </>);
    case "formwork":
      return (<>
        <line x1={20} y1={70} x2={300} y2={70} stroke={AMBER} strokeWidth={1.5} />
        <rect x={110} y={95} width={100} height={45} fill="#E2E8F0" stroke={GREY} strokeWidth={1} strokeDasharray="3 3" />
        <Note x={160} y={120} text={L("خرسانة", "concrete")} color={GREY} anchor="middle" />
        <rect x={98} y={90} width={12} height={55} fill="#FCD9B6" stroke={INK} strokeWidth={1.4} />
        <rect x={210} y={90} width={12} height={55} fill="#FCD9B6" stroke={INK} strokeWidth={1.4} />
        <line x1={98} y1={92} x2={70} y2={145} stroke="#B45309" strokeWidth={1.6} />
        <line x1={222} y1={92} x2={250} y2={145} stroke="#B45309" strokeWidth={1.6} />
        <Dim x1={98} y1={80} x2={222} y2={80} label={L("خنزيرة القاعدة", "footing shutter")} />
        <Note x={200} y={172} text={L("قبل الصب: استقامة + منسوب + إحكام", "before pour: line + level + tight")} color={GREEN} anchor="middle" />
      </>);
    case "rebar":
      return (<>
        <rect x={110} y={40} width={90} height={120} fill="#F1F5F9" stroke={GREY} strokeWidth={1} />
        {[52,80,108,136].map((y,i)=>(<rect key={i} x={120} y={y} width={70} height={16} fill="none" stroke={RED} strokeWidth={1.1} />))}
        {[122,188].map((cx)=>(<line key={cx} x1={cx} y1={46} x2={cx} y2={156} stroke={INK} strokeWidth={2} />))}
        <Dim x1={110} y1={30} x2={200} y2={30} label={L("قضبان طولية", "main bars")} />
        <Note x={210} y={70} text={L("كانات Ø8@200", "ties Ø8@200")} color={RED} />
        <Note x={155} y={185} text={L("تقديري - يعتمده مهندس مرخّص + تقرير تربة", "estimate - licensed engineer + soil report")} color={RED} anchor="middle" />
      </>);
    case "light_well":
      return (<>
        <line x1={130} y1={30} x2={130} y2={165} stroke={INK} strokeWidth={2} />
        <line x1={190} y1={30} x2={190} y2={165} stroke={INK} strokeWidth={2} />
        <rect x={40} y={60} width={90} height={105} fill="#F8FAFC" stroke={GREY} strokeWidth={1} />
        <rect x={190} y={60} width={90} height={105} fill="#F8FAFC" stroke={GREY} strokeWidth={1} />
        <Note x={85} y={120} text={L("غرفة", "room")} color={GREY} anchor="middle" />
        <Note x={235} y={120} text={L("غرفة مجاورة", "adjacent room")} color={GREY} anchor="middle" />
        <rect x={124} y={78} width={6} height={22} fill={BLUE} />
        <rect x={190} y={78} width={6} height={22} fill={BLUE} />
        {[145,160,175].map((x,i)=>(<line key={"d"+i} x1={x} y1={30} x2={x} y2={70} stroke={AMBER} strokeWidth={1} strokeDasharray="3 3" />))}
        {[150,170].map((x,i)=>(<line key={"h"+i} x1={x} y1={160} x2={x} y2={46} stroke={RED} strokeWidth={1.2} strokeDasharray="4 3" />))}
        {[150,170].map((x,i)=>(<polygon key={"ha"+i} points={`${x-4},50 ${x+4},50 ${x},40`} fill={RED} />))}
        <Dim x1={130} y1={176} x2={190} y2={176} label={L("1.0–2.0 م", "1.0–2.0 m")} above={false} />
        <Note x={160} y={26} text={L("مكشوف للسماء", "open to sky")} color={AMBER} anchor="middle" />
        <Note x={160} y={195} text={L("نوافذ الغرف تفتح على المنور - إنارة + تهوية", "room windows open onto well - daylight + vent")} color={GREEN} anchor="middle" />
      </>);
    case "room":
      return (<>
        <rect x={70} y={45} width={180} height={110} fill="#F8FAFC" stroke={INK} strokeWidth={2} />
        <rect x={70} y={120} width={26} height={6} fill="#fff" stroke={INK} strokeWidth={1.4} />
        <path d="M96 120 A26 26 0 0 1 96 146" fill="none" stroke="#B45309" strokeWidth={1} strokeDasharray="3 2" />
        <rect x={150} y={45} width={40} height={6} fill={BLUE} />
        <Note x={112} y={135} text={L("باب", "door")} color="#B45309" />
        <Note x={170} y={42} text={L("نافذة/منور", "window/well")} color={BLUE} anchor="middle" />
        <Dim x1={70} y1={34} x2={250} y2={34} label={L("العرض", "width")} />
        <Dim x1={60} y1={45} x2={60} y2={155} label={L("العمق", "depth")} color={GREEN} />
        <Note x={160} y={100} text={L("فراغ وظيفي", "functional space")} color={GREY} anchor="middle" />
      </>);
    case "corridor":
      return (<>
        <rect x={40} y={80} width={240} height={40} fill="url(#ddHatch)" stroke="#64748B" strokeWidth={1.4} strokeDasharray="5 3" />
        <line x1={50} y1={100} x2={260} y2={100} stroke={GREEN} strokeWidth={1.6} markerEnd="url(#ddArr)" />
        <Dim x1={295} y1={80} x2={295} y2={120} label={L("≥0.9م", "≥0.9m")} color={GREEN} />
        <Note x={160} y={70} text={L("موزّع الحركة إلى الغرف", "circulation to rooms")} color="#64748B" anchor="middle" />
        <Note x={160} y={145} text={L("لا يمر عبر أي غرفة خاصة", "never passes through a private room")} color={GREY} anchor="middle" />
      </>);
    case "stair":
      return (<>
        <rect x={90} y={40} width={120} height={120} fill="#F1F5F9" stroke={INK} strokeWidth={1.6} />
        {[0,1,2,3,4,5,6].map((k)=>(<line key={k} x1={90} y1={40+k*17} x2={210} y2={40+k*17} stroke="#94A3B8" strokeWidth={0.9} />))}
        <line x1={150} y1={155} x2={150} y2={45} stroke={INK} strokeWidth={1.8} markerEnd="url(#ddArr)" />
        <Note x={150} y={100} text="UP ↑" color={INK} anchor="middle" />
        <Dim x1={225} y1={40} x2={225} y2={57} label={L("قائمة 17", "riser 17")} color={GREEN} />
        <Note x={150} y={185} text={L("قائمة 17-18سم · نائمة 28-30سم", "riser 17-18 · tread 28-30")} color={GREY} anchor="middle" />
      </>);
    case "entry":
      return (<>
        <rect x={60} y={90} width={200} height={40} fill="#F8FAFC" stroke={INK} strokeWidth={2} />
        <rect x={140} y={124} width={40} height={8} fill="#fff" stroke={INK} strokeWidth={1.6} />
        <line x1={160} y1={175} x2={160} y2={132} stroke={RED} strokeWidth={2.6} markerEnd="url(#ddArrRed)" />
        <Note x={160} y={70} text={L("الواجهة الأمامية (الشارع)", "front facade (street)")} color={RED} anchor="middle" />
        <Note x={160} y={195} text={L("سهم اتجاه الدخول للمبنى", "entry direction into the building")} color={GREY} anchor="middle" />
      </>);
    case "shop":
      return (<>
        <rect x={70} y={45} width={180} height={110} fill="#FEF3C7" stroke={INK} strokeWidth={2} />
        <rect x={70} y={145} width={180} height={10} fill={BLUE} />
        <Note x={160} y={40} text={L("محل - واجهة زجاجية", "shop - glazed front")} color={AMBER} anchor="middle" />
        <Dim x1={70} y1={168} x2={250} y2={168} label={L("واجهة 4-6م", "frontage 4-6m")} above={false} />
        <Dim x1={60} y1={45} x2={60} y2={155} label={L("عمق 6-12م", "depth 6-12m")} color={GREEN} />
      </>);
    case "parking":
      return (<>
        {[0,1,2].map((k)=>(<rect key={k} x={60+k*70} y={50} width={60} height={100} fill="#F1F5F9" stroke={INK} strokeWidth={1.4} />))}
        <line x1={40} y1={160} x2={280} y2={160} stroke="#64748B" strokeWidth={1.4} strokeDasharray="6 4" />
        <Dim x1={60} y1={40} x2={120} y2={40} label={L("2.5م", "2.5m")} />
        <Dim x1={40} y1={50} x2={40} y2={150} label={L("5.0م", "5.0m")} color={GREEN} />
        <Note x={160} y={185} text={L("ممر مناورة 6.0م", "aisle 6.0m")} color="#64748B" anchor="middle" />
      </>);
    default:
      return null;
  }
}

export default function DetailDiagram({ kind, ar }) {
  if (!kind) return null;
  const body = render(kind, ar);
  if (!body) return null;
  return (
    <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #CBD5E1", background: "#fff" }} data-testid={`detail-diagram-${kind}`}>
      <div style={{ fontSize: 11, fontWeight: 900, color: "#0F172A", padding: "8px 10px 2px" }}>
        {ar ? "مخطط تفصيلي - دليل التنفيذ" : "Construction detail - build guide"}
      </div>
      <svg width="100%" height={200} viewBox="0 0 320 200">
        <defs>
          <marker id="ddArr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#0F172A" />
          </marker>
          <marker id="ddArrRed" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#DC2626" />
          </marker>
          <pattern id="ddHatch" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="7" height="7" fill="#F1F5F9" />
            <line x1="0" y1="0" x2="0" y2="7" stroke="#94A3B8" strokeWidth="0.8" />
          </pattern>
        </defs>
        <rect x={0} y={0} width={320} height={200} fill="#FFFFFF" />
        {body}
      </svg>
    </div>
  );
}
